import Card from '@mui/material/Card';
import { ThemeProvider } from '@mui/material';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { filterUndefined } from '../../utils/FilterUndefinedObject';
import DefaultImage from '../../static/images/default_thumbnail.png';
import './LessonCard.scss';
import ObjectUtils from '../../utils/ObjectUtils';
import { getActivityCardTitle, getActivityDetailsLine, getActivityTypeTag } from '../../utils/ActivityCardInfo';
import materialTheme from '../../materialUITheme';

const cardRatio = 1.24;
const imageRatio = 0.4;

export const CARD_VARIANT_ACTIVITY = 'activity';
export const ACTIVITY_CARD_WIDTH = 200;

/**
 * Lesson card for displaying a lesson
 *
 * @param {*} prop
 * lessonData - data to display on the card
 * width - the width of the card
 * showFullTitle - display the whole title and let the card grow to fit it
 * cardVariant - 'activity' for the white activity card (full title, or Unit - Topic for course
 *  activities, type tag, divider and Unit • Topic • Level details, no description); the default
 *  keeps the dark card
 * activityFooterLabel - node shown under the details line of the activity card
 * activityDetailsLine - details text of the activity card in place of Unit • Topic • Level
 * actions - array of action on the card. The shape of the object is
 * {
 *  icon: node - the icon of the action to display on the card,
 *  action: func - action handler of the action,
 * }
 */
const defaultProps = {
  width: 205,
  actions: [],
  isHighligthed: false,
  cardLabels: [],
  showLessonDescription: true,
  showLessonBadges: true,
  showFullTitle: false,
  cardVariant: undefined,
  activityFooterLabel: null,
  activityDetailsLine: undefined,
};

const getActivityTestId = (name, uuid) => `${name}-${uuid}`;

function ActivityCardContent({ lessonData, footerLabel, detailsLine }) {
  const { attributes } = lessonData;
  const typeTag = getActivityTypeTag(attributes);
  const testId = (name) => getActivityTestId(name, attributes.uuid);

  return (
    <CardContent className='lesson-card-content' data-test={testId('lesson-card-content')}>
      <Typography variant='h5' component='h2' className='lesson-card-title' data-test={testId('lesson-card-title')} data-dd-privacy='mask'>
        {getActivityCardTitle(attributes)}
      </Typography>
      <div className='lesson-card-type-tag-row' data-test={testId('lesson-card-type-tag-row')}>
        {typeTag && (
          <span className={`lesson-card-type-tag lesson-card-type-tag--${typeTag.colorKey}`} data-test={testId('lesson-card-type-tag')}>
            {typeTag.label}
          </span>
        )}
      </div>
      <div className='lesson-card-footer' data-test={testId('lesson-card-footer')}>
        <div className='lesson-card-divider' data-test={testId('lesson-card-divider')} />
        <div className='lesson-card-details' data-test={testId('lesson-card-details')}>
          {detailsLine !== undefined ? detailsLine : getActivityDetailsLine(attributes)}
        </div>
        {footerLabel}
      </div>
    </CardContent>
  );
}

export default function LessonCard(props) {
  const {
    lessonData,
    width,
    actions,
    isHighligthed,
    cardLabels,
    showLessonDescription,
    showLessonBadges,
    showFullTitle,
    cardVariant,
    activityFooterLabel,
    activityDetailsLine,
    ...rest
  } = { ...defaultProps, ...filterUndefined(props) };
  const cardHeight = width * cardRatio;
  const imageHeight = imageRatio * cardHeight;
  const { attributes } = lessonData;
  const isActivityCard = cardVariant === CARD_VARIANT_ACTIVITY;
  const hasSpecialtyIcon = lessonData.specialtyContent && !ObjectUtils.isEmpty(lessonData.specialtyContent);
  // The default card keeps its original data-test values; only the activity card gets per-lesson ones
  const activityTestId = (name) => (isActivityCard ? getActivityTestId(name, attributes.uuid) : undefined);
  const sharedTestId = (name) => activityTestId(name) || name;

  return (
    <ThemeProvider theme={materialTheme}>
      <Card
        theme={materialTheme}
        data-lessonuuid={lessonData.attributes.uuid}
        className={`lesson-card-container ${isHighligthed ? 'highlighted' : ''}${showFullTitle ? ' full-title' : ''}${isActivityCard ? ' activity-card' : ''}`}
        style={showFullTitle || isActivityCard ? { width, minHeight: cardHeight } : { width, height: cardHeight }}
        data-test={`lesson-card-${lessonData.attributes.uuid}`}
        tabIndex={0}
        {...rest}
      >
        <CardActionArea className='lesson-card-container-action-area' tabIndex='-1' type='button'>
          {lessonData.attributes.hasVideo && lessonData.attributes.videoUrl ? (
            <div className='lesson-card-video' style={{ height: imageHeight }} data-test={activityTestId('lesson-card-video')}>
              <video src={`${lessonData.attributes.videoUrl}#t=0.1`} type='video/mp4'>
                <track kind='captions' src={`${lessonData.attributes.videoUrl}#t=0.1`} />
              </video>
            </div>
          ) : (
            <CardMedia
              style={{ height: imageHeight }}
              image={attributes.thumbnailUrl ? attributes.thumbnailUrl : DefaultImage}
              title={attributes.name}
              data-test={sharedTestId('lesson-card-media')}
            />
          )}
          {isActivityCard ? (
            <ActivityCardContent lessonData={lessonData} footerLabel={activityFooterLabel} detailsLine={activityDetailsLine} />
          ) : (
            <CardContent className='lesson-card-content'>
              {!hasSpecialtyIcon ? (
                ''
              ) : (
                <img
                  className='lesson-card-specialty-icon'
                  src={lessonData.specialtyContent.cardImage}
                  alt='Specialty Logo'
                  data-test='lesson-card-specialty-icon'
                />
              )}
              <div className='arrow-up'>&nbsp;</div>
              <Typography gutterBottom variant='h5' component='h2' className='lesson-card-title' data-test='lesson-card-title' data-dd-privacy='mask'>
                {attributes.name}
              </Typography>
              {showLessonDescription && <p className='lesson-card-description' data-dd-privacy='mask'>{attributes.description}</p>}
            </CardContent>
          )}
          {isActivityCard && hasSpecialtyIcon && (
            <img
              className='lesson-card-specialty-icon'
              src={lessonData.specialtyContent.cardImage}
              alt='Specialty Logo'
              data-test={activityTestId('lesson-card-specialty-icon')}
            />
          )}
        </CardActionArea>
        {showLessonBadges && attributes.subjects && attributes.subjects.length > 0 ? (
          <div className='badges' data-test={activityTestId('lesson-card-badges')}>
            <div className='badge'>{attributes.subjects.map((subject) => (subject === 'English Language Arts' ? 'ELA' : subject))}</div>
          </div>
        ) : (
          ''
        )}

        <div className='lesson-labels' data-test={activityTestId('lesson-card-labels')}>{cardLabels.map((label) => label)}</div>

        <div className='lesson-action-overlay' data-test={activityTestId('lesson-card-actions')}>{actions.map((action) => action)}</div>
      </Card>
    </ThemeProvider>
  );
}
